import { Box, Button, Stack, Typography } from '@mui/material';
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { UruannaMark } from '@/components/brand/UruannaMark';
import { useOnboarding } from '@/hooks/useOnboarding';
import { paths } from '@/routes/paths';
import { fontFamilies, radii } from '@/theme/tokens';

/**
 * The error pages (Errors board): one centred card — the still mark, the
 * status as a pale 88px numeral, a 22px title, one or two sentences, and the
 * way on as full-width buttons. 404 offers the apps and the way back, 403 the
 * apps, 500 a retry first and the apps second, with the request id in mono so
 * a person can quote it.
 *
 * The 403 board also names the organization's owner as the person to ask.
 * The names come from `GET /onboarding` → `ownerNames` (backend 628d9f9),
 * read from the query the shell already holds — no request of its own.
 */
export function ErrorPage({
  code,
  title,
  body,
  requestId,
  detail,
  muted,
  children,
}: {
  /** The pale numeral; omitted for a failure that has no status. */
  code?: string;
  title: string;
  body: string;
  requestId?: string;
  /** A technical line for a crash (the error's message), in mono. */
  detail?: string;
  /** The 403 card draws the mark at 40%: a door, not a breakage. */
  muted?: boolean;
  /** The actions, first one primary, stacked full width. */
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh', px: 2, py: 4 }}>
      <Box
        component="section"
        aria-labelledby="error-page-title"
        sx={{
          width: '100%',
          maxWidth: 420,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: `${radii.xl}px`,
          px: { xs: 3, sm: 4.5 },
          py: 5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Box sx={{ opacity: muted ? 0.4 : 1 }}>
          <UruannaMark size={64} decorative />
        </Box>
        {code ? (
          <Box
            component="span"
            aria-hidden
            dir="ltr"
            sx={{
              mt: 2.5,
              fontFamily: fontFamilies.sans,
              fontSize: 88,
              lineHeight: 1,
              fontWeight: 600,
              letterSpacing: '-0.02em',
              color: 'divider',
            }}
          >
            {code}
          </Box>
        ) : null}
        <Typography id="error-page-title" component="h1" sx={{ mt: 2.5, fontSize: '1.375rem', fontWeight: 600, lineHeight: 1.35 }}>
          {title}
        </Typography>
        <Typography sx={{ mt: 1.25, fontSize: '0.9375rem', lineHeight: 1.7, color: 'text.secondary', maxWidth: 310 }}>
          {body}
        </Typography>
        {requestId ? (
          <Box sx={{ mt: 1.75, display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: '0.75rem', color: 'text.disabled' }}>
            {t('errorPage.requestId')}
            <Box
              component="code"
              dir="ltr"
              sx={{
                unicodeBidi: 'isolate',
                fontFamily: fontFamilies.mono,
                fontSize: '0.75rem',
                color: 'text.secondary',
                px: 0.75,
                py: 0.25,
                borderRadius: `${radii.xs}px`,
                bgcolor: 'brand.well',
              }}
            >
              {requestId}
            </Box>
          </Box>
        ) : null}
        {detail ? (
          <Box
            component="code"
            dir="ltr"
            sx={{
              mt: 1.75,
              maxWidth: '100%',
              overflowWrap: 'anywhere',
              fontFamily: fontFamilies.mono,
              fontSize: '0.75rem',
              color: 'text.secondary',
              px: 0.75,
              py: 0.25,
              borderRadius: `${radii.xs}px`,
              bgcolor: 'brand.well',
            }}
          >
            {detail}
          </Box>
        ) : null}
        {children ? (
          <Stack spacing={1.25} sx={{ mt: 3.5, alignSelf: 'stretch', px: { xs: 0, sm: 3 } }}>
            {children}
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
}

/**
 * "Back to apps" — to the grid, someone who has just hit a wall wants to see
 * their doors (CLAUDE.md, the apps grid). `routerless` is a plain link for a
 * crash caught above the router, which also reloads the bundle.
 */
export function BackToAppsButton({ variant = 'contained', routerless }: { variant?: 'contained' | 'outlined'; routerless?: boolean }) {
  const { t } = useTranslation();
  const common = {
    variant,
    color: variant === 'outlined' ? ('inherit' as const) : ('primary' as const),
    startIcon: <AppsRoundedIcon sx={{ fontSize: 18 }} />,
    fullWidth: true,
  };
  return routerless ? (
    <Button {...common} href={paths.modules}>
      {t('errorPage.backToApps')}
    </Button>
  ) : (
    <Button {...common} component={Link} to={paths.modules}>
      {t('errorPage.backToApps')}
    </Button>
  );
}

/** 404 — a route that does not exist. */
export function NotFoundState() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <ErrorPage code="404" title={t('errorPage.notFound.title')} body={t('errorPage.notFound.body')}>
      <BackToAppsButton />
      <Button variant="outlined" color="inherit" fullWidth onClick={() => navigate(-1)}>
        {t('errorPage.goBack')}
      </Button>
    </ErrorPage>
  );
}

/** 403 — a page the viewer's role does not open. */
export function ForbiddenState() {
  const { t, i18n } = useTranslation();
  const { status } = useOnboarding();
  /*
   * `ownerNames` is absent for platform staff and for a person with no
   * organization yet, and `[]` when no owner is active. Both mean the same
   * here: the generic sentence, which already says the owner grants access,
   * and no name. Only a non-empty list adds the names.
   */
  const owners = status?.ownerNames ?? [];
  const body = owners.length
    ? `${t('errorPage.forbidden.body')} ${t('errorPage.forbidden.askOwner', {
        count: owners.length,
        names: listOf(owners, i18n.language),
      })}`
    : t('errorPage.forbidden.body');
  return (
    <ErrorPage code="403" muted title={t('errorPage.forbidden.title')} body={body}>
      <BackToAppsButton />
    </ErrorPage>
  );
}

/**
 * 500 — the server failed or could not be reached. Retry first; the request
 * id when the failed response carried one.
 */
export function ServerErrorState({
  requestId,
  onRetry,
  routerless,
}: {
  requestId?: string;
  onRetry?: () => void;
  routerless?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <ErrorPage code="500" title={t('errorPage.server.title')} body={t('errorPage.server.body')} requestId={requestId}>
      <Button
        variant="contained"
        fullWidth
        startIcon={<RefreshRoundedIcon sx={{ fontSize: 18 }} />}
        onClick={onRetry ?? (() => window.location.reload())}
      >
        {t('common.retry')}
      </Button>
      <BackToAppsButton variant="outlined" routerless={routerless} />
    </ErrorPage>
  );
}

/**
 * The Errors board's server card, for a failure on our side of the wire: no
 * status and no request id, the error's own message in mono for whoever is
 * asked about it. The boundary may sit above the router, so the way out is a
 * plain link, which also reloads the bundle that crashed.
 */
export function CrashState({ message }: { message: string }) {
  const { t } = useTranslation();
  return (
    <ErrorPage title={t('errorPage.crash.title')} body={t('errorPage.crash.body')} detail={message}>
      <Button
        variant="contained"
        fullWidth
        startIcon={<RefreshRoundedIcon sx={{ fontSize: 18 }} />}
        onClick={() => window.location.reload()}
      >
        {t('common.retry')}
      </Button>
      <BackToAppsButton variant="outlined" routerless />
    </ErrorPage>
  );
}

/** "A or B" in the reader's language — the person may ask any one owner. */
function listOf(names: string[], language: string): string {
  try {
    return new Intl.ListFormat(language, { type: 'disjunction' }).format(names);
  } catch {
    return names.join(', ');
  }
}
